import {
  importApi,
  type AnalyticsImport,
  type Project,
} from '@shared/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, Upload } from 'lucide-react';
import * as React from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useFormError } from '@/hooks/use-form-errors';
import { formatCompactNumber, formatRelative } from '@/lib/utils';

const STATUS_TONE: Record<
  AnalyticsImport['status'],
  'neutral' | 'accent' | 'success' | 'warning' | 'critical'
> = {
  pending_upload: 'neutral',
  processing: 'accent',
  completed: 'success',
  failed: 'critical',
};

const describeStatus = (row: AnalyticsImport): string => {
  switch (row.status) {
    case 'pending_upload':
      return 'Waiting for upload';
    case 'processing':
      return 'Processing';
    case 'completed':
      return 'Imported';
    case 'failed':
      return 'Failed';
    default:
      return row.status;
  }
};

const formatDay = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString(undefined, { dateStyle: 'medium' })
    : null;
};

const ImportPlausibleDialog = ({
  project,
  open,
  onOpenChange,
}: {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [file, setFile] = React.useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const close = () => {
    setFile(null);
    clearError();
    onOpenChange(false);
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) {
        throw new Error('Choose the Plausible export zip first.');
      }
      const { uploadUrl } = await importApi.createImport(project.id, {
        provider: 'plausible',
      });
      await importApi.uploadImportArchive(uploadUrl, file);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imports', project.id] });
      close();
    },
    onError: captureError,
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : close())}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            clearError();
            mutation.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Import from Plausible</DialogTitle>
            <DialogDescription>
              Bring your historical traffic across so trends do not restart at
              zero. Custom goals stay as event counts; session-level detail
              cannot be reconstructed from an export.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <div className="space-y-4">
              {error ? <Alert tone="error">{error}</Alert> : null}

              <div className="rounded-lg border border-border bg-surface-muted px-3 py-2.5 text-xs text-ink-muted">
                In Plausible, open your site&apos;s{' '}
                <span className="font-medium text-ink">
                  Settings &rarr; Imports &amp; Exports
                </span>{' '}
                and choose{' '}
                <span className="font-medium text-ink">Export to CSV</span>. You
                get a zip by email or download; upload it here unchanged. The
                chart download button exports something different and will not
                work.
              </div>

              <Field
                label="Plausible export (.zip)"
                htmlFor={`plausible-zip-${project.id}`}
                hint="Days already covered by GainSignal tracking are skipped automatically."
              >
                <Input
                  id={`plausible-zip-${project.id}`}
                  ref={fileInputRef}
                  name="archive"
                  type="file"
                  accept=".zip,application/zip"
                  required
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                />
              </Field>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              <Upload />
              Upload and import
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ImportsSection = ({
  project,
  canManage,
}: {
  project: Project;
  canManage: boolean;
}) => {
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [importOpen, setImportOpen] = React.useState(false);

  const importsQuery = useQuery({
    queryKey: ['imports', project.id],
    queryFn: () => importApi.listImports(project.id),
    // Processing finishes asynchronously; keep the status truthful while this
    // settings panel is open without requiring a manual refresh.
    refetchInterval: 5_000,
  });

  const deleteMutation = useMutation({
    mutationFn: (importId: string) =>
      importApi.deleteImport(project.id, importId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imports', project.id] });
    },
    onError: captureError,
  });

  const imports = importsQuery.data?.imports ?? [];

  return (
    <div className="px-5 py-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
          Imports
        </h3>
        {canManage ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              clearError();
              setImportOpen(true);
            }}
          >
            <Upload />
            Import from Plausible
          </Button>
        ) : null}
      </div>

      {error ? (
        <Alert tone="error" className="mb-3">
          {error}
        </Alert>
      ) : null}

      {importsQuery.isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : imports.length === 0 ? (
        <p className="py-2 text-sm text-ink-muted">
          No historical data imported. Bring your Plausible history across so
          trends and comparisons include it.
        </p>
      ) : (
        <ul className="space-y-2">
          {imports.map((row) => {
            const from = formatDay(row.dateFrom);
            const to = formatDay(row.dateTo);
            return (
              <li
                key={row.id}
                className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                    Plausible
                    <Badge tone={STATUS_TONE[row.status]}>
                      {describeStatus(row)}
                    </Badge>
                  </p>
                  <p className="mt-0.5 truncate text-xs text-ink-muted">
                    {row.status === 'completed' && from && to
                      ? `${from} – ${to} · ${formatCompactNumber(row.importedRows ?? 0)} rows`
                      : row.status === 'failed'
                        ? (row.errorMessage ?? 'The archive could not be read.')
                        : 'Historical daily aggregates'}
                  </p>
                </div>

                <span className="hidden text-xs text-ink-subtle sm:block">
                  uploaded {formatRelative(row.createdAt)}
                </span>

                {canManage ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remove import"
                    loading={
                      deleteMutation.isPending &&
                      deleteMutation.variables === row.id
                    }
                    onClick={() => {
                      clearError();
                      deleteMutation.mutate(row.id);
                    }}
                  >
                    <Trash2 />
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <ImportPlausibleDialog
        project={project}
        open={importOpen}
        onOpenChange={setImportOpen}
      />
    </div>
  );
};
